import { JockeyOne_400Regular } from '@expo-google-fonts/jockey-one';
import {
  Montserrat_700Bold,
  Montserrat_400Regular,
  Montserrat_500Medium,
} from '@expo-google-fonts/montserrat';
import { Raleway_700Bold, Raleway_400Regular } from '@expo-google-fonts/raleway';
import { useFonts } from 'expo-font';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, Image, Text, Pressable } from 'react-native';
import Animated, {
  useAnimatedRef,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets, useSafeAreaFrame } from 'react-native-safe-area-context';

import ExitCard from '../../components/ExitCard';
import ExplorationCard from '../../components/ExplorationCard';
import ExploreButton from '../../components/ExploreButton';
import HeaderViewDown from '../../components/HeaderViewDown';
import InstructionsCard from '../../components/InstructionsCard';
import ObjectsPositioning from '../../components/ObjectsPositioning';
import PlayButton from '../../components/PlayButton';
import QuestionCard from '../../components/QuestionCard';
import RankingCard from '../../components/RankingCard';
import ScoreCard from '../../components/ScoreCard';
import { useFloor } from '../../context/FloorContext';
import { useGame } from '../../context/GameContext';
import { useFloorNavigation } from '../../context/useFloorNavigation';
import { useGameFlowControls } from '../../context/useGameFlowControls';
import { imagePaths, initialTime } from '../../data/staticData';
import { styles } from './styles';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);
const AnimatedLinear = Animated.createAnimatedComponent(LinearGradient);

const Home = () => {
  const { height, width } = useSafeAreaFrame();
  const insets = useSafeAreaInsets();
  const scrollViewRef = useAnimatedRef();

  const translateXBackground = useSharedValue(-width * 0.1);

  const exploreFilterOpacity = useSharedValue(0);

  const {
    gameType,
    setGameType,
    corrects,
    setCorrects,
    errors,
    setErrors,
    isPlaying,
    setIsPlaying,
    timeFinished,
    setTimeFinished,
    renderScore,
    setRenderScore,
    finalScore,
    setFinalScore,
    handleObjectFound,
    handleScore,
    selectedObject,
    setSelectedObject,
    attempts,
    incrementAttempt,
    resetAttempts,
    MAX_ATTEMPTS,
  } = useGame();

  const {
    handleFirstFloorClick,
    handleSecondFloorClick,
    handleThirdFloorClick,
    handleRoofTopClick,
  } = useFloorNavigation();

  const {
    firstFloorFrontStyle,
    translateSide,
    firstFloorSideStyle,
    translateSecondFloorSide,
    secondFloorSideStyle,
    secondFloorFrontStyle,
    thirdFloorSideStyle,
    thirdFloorFrontStyle,
    roofTopFrontStyle,
  } = useFloor();

  const [lastScoreId, setLastScoreId] = useState('');
  const [renderRanking, setRenderRanking] = useState(false);
  const [renderExitCard, setRenderExitCard] = useState(false);
  const [givenUp, setGivenUp] = useState(false);
  const [renderInstructionsCard, setRenderInstructionsCard] = useState(false);
  const [hasEndedGame, setHasEndedGame] = useState(false);

  const {
    handlePlay,
    handleExplore,
    handleEnd: originalHandleEnd,
  } = useGameFlowControls({
    setGameType,
    setRenderInstructionsCard,
    scrollViewRef,
    setIsPlaying,
    setCorrects,
    setErrors,
    setFinalScore,
    setSelectedObject,
    setRenderExitCard,
    setTimeFinished,
    exploreFilterOpacity,
    resetAttempts,
  });

  // Wrapper to reset hasEndedGame when ending the game
  const handleEnd = useCallback(() => {
    setHasEndedGame(false);
    originalHandleEnd();
  }, [originalHandleEnd]);

  // Wrapper to reset hasEndedGame when starting a new game
  const handlePlayWrapper = useCallback(() => {
    setHasEndedGame(false);
    setIsPlaying(false);
    setTimeFinished(false);
    setGivenUp(false);
    handlePlay();
  }, [handlePlay]);

  const handleExploreWrapper = useCallback(() => {
    setHasEndedGame(false);
    handleExplore();
  }, [handleExplore]);

  const [time, setTime] = useState(initialTime);
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(initialTime);

  useEffect(() => {
    let timerId;

    if (isPlaying) {
      timerId = setInterval(() => {
        timerRef.current -= 1;
        if (timerRef.current < 0) {
          clearInterval(timerId);
        } else {
          setTimeLeft(timerRef.current);
          setTime(timerRef.current);
        }
      }, 1000);
    } else {
      timerRef.current = initialTime;
      setTime(initialTime);
    }

    return () => {
      clearInterval(timerId);
    };
  }, [isPlaying]);

  const exploreFilterStyle = useAnimatedStyle(() => {
    return {
      opacity: 0.4,
      height,
      width: '100%',
      bottom: 0.062 * height,
      position: 'absolute',
    };
  }, []);

  useEffect(() => {
    if (!hasEndedGame && (timeFinished || attempts >= MAX_ATTEMPTS || givenUp)) {
      setHasEndedGame(true);
      handleScore(timeLeft);
    }
  }, [attempts, timeFinished, givenUp, MAX_ATTEMPTS, handleScore, timeLeft, hasEndedGame]);

  useEffect(() => {
    translateXBackground.value = withRepeat(
      withTiming(width * 0.1, {
        duration: 20000,
      }),
      -1,
      true
    );
  }, [width]);

  const imageStyle = useAnimatedStyle(() => {
    return {
      height: 1.7 * height,
      width: 1.22890625 * 1.7 * height * 2,
      position: 'absolute',
      alignSelf: 'center',
      transform: [{ translateX: translateXBackground.value }],
    };
  }, [translateXBackground]);

  const [fontsLoaded] = useFonts({
    Raleway_700Bold,
    Raleway_400Regular,
    Montserrat_700Bold,
    Montserrat_400Regular,
    JockeyOne_400Regular,
    Montserrat_500Medium,
  });

  if (!fontsLoaded) {
    return null;
  }

  return (
    <>
      <View style={{ flexDirection: 'row' }}>
        <Animated.ScrollView
          scrollEnabled={false}
          ref={scrollViewRef}
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          style={{
            height,
            width,
            alignSelf: 'center',
          }}>
          <Animated.Image source={imagePaths.background2} style={imageStyle} />

          <View
            style={{
              height: 0.7 * height,
              width: '100%',
              paddingTop: insets.top,
            }}>
            <View style={{ flex: 1, justifyContent: 'center', alignSelf: 'center', maxWidth: 900 }}>
              <Image
                source={imagePaths.logo}
                style={{ alignSelf: 'center', height: height * 0.09, width: 4.21 * height * 0.09 }}
              />

              <Text
                style={[
                  styles.subtitle,
                  {
                    fontSize: 0.0187 * height,
                    marginTop: 0.02 * height,
                  },
                ]}>
                {
                  'A composição dos objetos  é complexa, muitas vezes envolvem mais de um único material. Isso é comum  também no universo dos produtos metálicos, compostos, frequentemente, por ligas.  A matéria prima para a produção dos metais é extraída de diversas regiões do Brasil e dão origem a uma infinidade de produtos. Vamos conhecer?\n\nQue tal testar seu conhecimento sobre a composição dos objetos do dia a dia?  Encontre os objetos no cenário e responda às perguntas. Vamos descobrir quem sabe mais!'
                }
              </Text>

              <PlayButton
                onPress={() => {
                  setRenderInstructionsCard(true);
                }}
              />

              <ExploreButton
                onPress={() => {
                  handleExploreWrapper();
                }}
              />
            </View>
          </View>

          <Pressable style={{ height, width: '100%' }}>
            <Image
              source={imagePaths.scenario2}
              style={{
                height,
                width: 2 * height,
                alignSelf: 'center',
                position: 'absolute',
              }}
            />

            <Image
              source={imagePaths.buildingBackground}
              style={{
                height: 0.82 * (height - insets.top - insets.bottom),
                width: 1.633 * 0.82 * (height - insets.top - insets.bottom),
                alignSelf: 'center',
                position: 'absolute',
                resizeMode: 'contain',
                bottom: 0.062 * height,
              }}
            />

            {gameType === 'explore' && (
              <AnimatedLinear
                start={[0.5, 0]}
                end={[0.5, 1]}
                locations={[0.75, 0.8, 0.85]}
                colors={['#000000CC', '#18181866', 'transparent']}
                style={exploreFilterStyle}
              />
            )}

            <HeaderViewDown
              setRenderExitCard={setRenderExitCard}
              objectsFound={attempts}
              maxAttempts={MAX_ATTEMPTS}
              isPlaying={isPlaying}
              setGameType={setGameType}
              gameType={gameType}
              setTimeFinished={setTimeFinished}
              handleEnd={handleEnd}
              setRenderRanking={setRenderRanking}
              time={time}
            />

            <ObjectsPositioning
              gameType={gameType}
              initialValue={translateSide}
              translateSecondFloorSide={translateSecondFloorSide}
              handleObjectFound={(object) => {
                if (
                  corrects[object.object] === undefined &&
                  errors[object.object] === undefined &&
                  attempts < MAX_ATTEMPTS
                ) {
                  handleObjectFound(object);
                }
              }}
              corrects={corrects}
              errors={errors}
            />

            <AnimatedPressable
              onPress={() => {
                handleFirstFloorClick();
              }}
              style={firstFloorSideStyle}>
              <Image source={imagePaths.firstFloorSide} style={{ height: '100%', width: '100%' }} />
            </AnimatedPressable>

            <AnimatedPressable
              style={firstFloorFrontStyle}
              onPress={() => {
                handleFirstFloorClick();
              }}>
              <Image
                source={imagePaths.firstFloorFront}
                style={{ height: '100%', width: '100%' }}
              />
            </AnimatedPressable>

            <AnimatedPressable
              onPress={() => {
                handleSecondFloorClick();
              }}
              style={secondFloorSideStyle}>
              <Image
                source={imagePaths.secondFloorSide}
                style={{ height: '100%', width: '100%' }}
              />
            </AnimatedPressable>

            <AnimatedPressable
              style={secondFloorFrontStyle}
              onPress={() => {
                handleSecondFloorClick();
              }}>
              <Image
                source={imagePaths.secondFloorFront}
                style={{ height: '100%', width: '100%' }}
              />
            </AnimatedPressable>

            <AnimatedPressable
              onPress={() => {
                handleThirdFloorClick();
              }}
              style={thirdFloorSideStyle}>
              <Image source={imagePaths.thirdFloorSide} style={{ height: '100%', width: '100%' }} />
            </AnimatedPressable>

            <AnimatedPressable
              style={thirdFloorFrontStyle}
              onPress={() => {
                handleThirdFloorClick();
              }}>
              <Image
                source={imagePaths.thirdFloorFront}
                style={{ height: '100%', width: '100%' }}
              />
            </AnimatedPressable>

            <AnimatedPressable
              style={roofTopFrontStyle}
              onPress={() => {
                handleRoofTopClick();
              }}>
              <Image source={imagePaths.roofTopFront} style={{ height: '100%', width: '100%' }} />
            </AnimatedPressable>
          </Pressable>
        </Animated.ScrollView>
      </View>

      {selectedObject && gameType === 'play' && (
        <QuestionCard
          setCorrects={setCorrects}
          setErrors={setErrors}
          selectedObject={selectedObject}
          setSelectedObject={setSelectedObject}
          incrementAttempt={incrementAttempt}
        />
      )}

      {selectedObject && gameType === 'explore' && (
        <ExplorationCard selectedObject={selectedObject} setSelectedObject={setSelectedObject} />
      )}

      {renderScore && (
        <ScoreCard
          time={timeLeft}
          finalScore={finalScore}
          handleEnd={handleEnd}
          setRenderScore={setRenderScore}
          setRenderRanking={setRenderRanking}
          setLastScoreId={setLastScoreId}
          onExitWithoutSave={() => {
            setHasEndedGame(false);
            setTimeFinished(false);
            setGivenUp(false);
          }}
        />
      )}
      {renderRanking && (
        <RankingCard setRenderRanking={setRenderRanking} lastScoreId={lastScoreId} />
      )}
      {renderExitCard && (
        <ExitCard
          setGivenUp={(value) => {
            setGivenUp(value);
            if (value) {
              setHasEndedGame(false);
            }
          }}
          setRenderExitCard={setRenderExitCard}
        />
      )}

      {renderInstructionsCard && <InstructionsCard handlePlay={handlePlayWrapper} />}
    </>
  );
};

export default Home;
